/**
 * Authentication hook using AWS Amplify/Cognito.
 * Provides authentication state and methods for the meetings feature.
 */

import { useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  updateUserAttributes as amplifyUpdateUserAttributes,
} from '@aws-amplify/auth';
import { createLogger } from '@/lib/logger';
import { captureError } from '@/lib/sentry';

const log = createLogger('useAuth');

/**
 * Map technical Cognito errors to user-friendly messages.
 * Technical details are logged to Sentry, not shown to users.
 */
function getUserFriendlyError(error: Error): string {
  const message = error.message || '';

  // Configuration/infrastructure errors - don't expose details
  if (message.includes('User pool client') && message.includes('does not exist')) {
    return 'Authentication service is temporarily unavailable. Please try again later.';
  }
  if (message.includes('User pool') && message.includes('does not exist')) {
    return 'Authentication service is temporarily unavailable. Please try again later.';
  }

  // Common user-facing errors - keep these messages
  if (message.includes('Incorrect username or password')) {
    return 'Incorrect email or password. Please try again.';
  }
  if (message.includes('User does not exist')) {
    return 'No account found with this email. Please check your email or sign up.';
  }
  if (message.includes('User is not confirmed')) {
    return 'Please verify your email before signing in.';
  }
  if (message.includes('Password attempts exceeded')) {
    return 'Too many failed attempts. Please try again later.';
  }
  if (message.includes('User already exists')) {
    return 'An account with this email already exists.';
  }
  if (message.includes('Invalid verification code')) {
    return 'Invalid verification code. Please check and try again.';
  }
  if (message.includes('Password did not conform')) {
    return 'Password must be at least 8 characters with uppercase, lowercase, and numbers.';
  }
  if (message.includes('Attempt limit exceeded')) {
    return 'Too many attempts. Please wait a few minutes before trying again.';
  }
  if (message.includes('Code mismatch') || message.includes('Invalid code')) {
    return 'Invalid reset code. Please check and try again.';
  }
  if (message.includes('expired')) {
    return 'Reset code has expired. Please request a new one.';
  }

  // Session/authentication errors
  if (message.includes('UserUnAuthenticatedException') || message.includes('needs to be authenticated')) {
    return 'Your session has expired. Please sign in again.';
  }

  // Network errors
  if (message.includes('Network') || message.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }

  // Default - don't expose unknown technical errors
  return 'An unexpected error occurred. Please try again.';
}

// Configure Amplify with Cognito settings
const userPoolId = import.meta.env.REACT_APP_USER_POOL_ID;
const clientId = import.meta.env.REACT_APP_CLIENT_ID;
const identityPoolId = import.meta.env.REACT_APP_IDENTITY_POOL_ID;

if (userPoolId && clientId) {
  log.debug('Configuring Amplify', {
    action: 'configure',
    userPoolId,
    clientIdLength: clientId.length,
    hasIdentityPool: !!identityPoolId
  });

  // Configure with or without Identity Pool (Identity Pool enables cloud document storage)
  if (identityPoolId) {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId,
          userPoolClientId: clientId,
          signUpVerificationMethod: 'code',
          identityPoolId,
        }
      }
    });
  } else {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId,
          userPoolClientId: clientId,
          signUpVerificationMethod: 'code',
        }
      }
    });
  }
} else {
  log.warn('Missing Cognito configuration', { action: 'configure', hasUserPoolId: !!userPoolId, hasClientId: !!clientId });
}

export interface User {
  userId: string;
  username: string;
  email?: string;
  fullName?: string;
  groups?: string[];
  plan?: string;
  avatarUrl?: string;
  avatarS3Key?: string;
  signupDate?: string;
}

export interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: string | null;
  getAccessToken: () => Promise<string | null>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, options?: { fullName?: string; plan?: string; signupDate?: string }) => Promise<void>;
  confirmRegistration: (email: string, code: string) => Promise<void>;
  resendVerificationCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  updateUserAttributes: (attributes: Record<string, string>) => Promise<void>;
  setAvatarUrl: (avatarUrl: string | undefined) => void;
}

export function useAuth(): UseAuthReturn {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checkAuth = useCallback(async () => {
    log.debug('Checking auth session', { action: 'checkAuth' });
    try {
      const session = await fetchAuthSession();
      if (session.tokens) {
        const currentUser = await getCurrentUser();

        // Extract groups and custom attributes from ID token payload
        const idTokenPayload = session.tokens.idToken?.payload;
        const groups = (idTokenPayload?.['cognito:groups'] as string[] | undefined) || [];
        const plan = (idTokenPayload?.['custom:plan'] as string | undefined);
        const fullName = (idTokenPayload?.['custom:fullName'] as string | undefined);
        const avatarS3Key = (idTokenPayload?.['custom:avatarS3Key'] as string | undefined);
        const signupDate = (idTokenPayload?.['custom:signupDate'] as string | undefined);

        log.info('User authenticated', {
          action: 'checkAuth',
          userId: currentUser.userId,
          username: currentUser.username,
          groups: groups.length > 0 ? groups : undefined,
          plan,
          hasAvatar: !!avatarS3Key,
        });
        setUser({
          userId: currentUser.userId,
          username: currentUser.username,
          email: currentUser.signInDetails?.loginId,
          fullName,
          groups,
          plan,
          avatarS3Key,
          signupDate,
        });
        setIsAuthenticated(true);
      } else {
        log.debug('No active session', { action: 'checkAuth' });
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Auth check failed');
      // Configuration errors should be logged to Sentry
      if (error.message.includes('does not exist') || error.message.includes('not configured')) {
        captureError(error, { action: 'checkAuth' });
      }
      log.debug('Auth check failed (user not logged in)', { action: 'checkAuth' });
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    log.debug('Fetching access token', { action: 'getAccessToken' });
    try {
      const session = await fetchAuthSession();
      // API Gateway Cognito authorizer with AuthorizationScopes validates access tokens
      const token = session.tokens?.accessToken?.toString() ?? null;
      if (token) {
        log.debug('Access token retrieved', { action: 'getAccessToken', tokenLength: token.length });
      } else {
        log.warn('No access token available', { action: 'getAccessToken', hasSession: !!session, hasTokens: !!session.tokens });
      }
      return token;
    } catch (err) {
      log.error('Failed to get access token', err instanceof Error ? err : new Error('Unknown error'), { action: 'getAccessToken' });
      return null;
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    log.debug('Attempting login', { action: 'login', username });
    setError(null);
    try {
      const result = await signIn({ username, password });

      if (!result.isSignedIn) {
        log.warn('Sign-in incomplete', {
          action: 'login',
          username,
          nextStep: result.nextStep?.signInStep,
        });
        throw new Error(
          result.nextStep?.signInStep === 'CONFIRM_SIGN_UP'
            ? 'Please verify your email before signing in.'
            : 'Sign in could not be completed. Please try again.'
        );
      }

      log.info('Login successful', { action: 'login', username });
      await checkAuth();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Login failed');
      log.error('Login failed', error, { action: 'login', username });
      // Log technical details to Sentry, show user-friendly message
      captureError(error, { action: 'login', username });
      const userMessage = getUserFriendlyError(error);
      setError(userMessage);
      throw new Error(userMessage);
    }
  }, [checkAuth]);

  const logout = useCallback(async () => {
    log.debug('Logging out', { action: 'logout' });
    try {
      await signOut();
      log.info('Logout successful', { action: 'logout' });
      setIsAuthenticated(false);
      setUser(null);
    } catch (err) {
      log.error('Logout failed', err instanceof Error ? err : new Error('Unknown error'), { action: 'logout' });
    }
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    options?: { fullName?: string; plan?: string; signupDate?: string }
  ) => {
    log.debug('Attempting registration', { action: 'register', email, plan: options?.plan });
    setError(null);
    try {
      // Build user attributes including optional custom attributes
      const userAttributes: Record<string, string> = { email };

      if (options?.fullName) {
        userAttributes['custom:fullName'] = options.fullName;
      }
      if (options?.plan) {
        userAttributes['custom:plan'] = options.plan;
      }
      if (options?.signupDate) {
        userAttributes['custom:signupDate'] = options.signupDate;
      }

      await signUp({
        username: email,
        password,
        options: {
          userAttributes,
        },
      });
      log.info('Registration successful', { action: 'register', email, plan: options?.plan });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Registration failed');
      log.error('Registration failed', error, { action: 'register', email });
      // Log technical details to Sentry, show user-friendly message
      captureError(error, { action: 'register', email });
      const userMessage = getUserFriendlyError(error);
      setError(userMessage);
      throw new Error(userMessage);
    }
  }, []);

  const confirmRegistration = useCallback(async (email: string, code: string) => {
    log.debug('Confirming registration', { action: 'confirmRegistration', email });
    setError(null);
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });
      log.info('Registration confirmed', { action: 'confirmRegistration', email });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Confirmation failed');
      log.error('Confirmation failed', error, { action: 'confirmRegistration', email });
      // Log technical details to Sentry, show user-friendly message
      captureError(error, { action: 'confirmRegistration', email });
      const userMessage = getUserFriendlyError(error);
      setError(userMessage);
      throw new Error(userMessage);
    }
  }, []);

  const resendVerificationCode = useCallback(async (email: string) => {
    log.debug('Resending verification code', { action: 'resendVerificationCode', email });
    setError(null);
    try {
      await resendSignUpCode({ username: email });
      log.info('Verification code resent', { action: 'resendVerificationCode', email });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to resend code');
      log.error('Failed to resend verification code', error, { action: 'resendVerificationCode', email });
      captureError(error, { action: 'resendVerificationCode', email });
      const userMessage = getUserFriendlyError(error);
      setError(userMessage);
      throw new Error(userMessage);
    }
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    log.debug('Requesting password reset', { action: 'forgotPassword', email });
    setError(null);
    try {
      await resetPassword({ username: email });
      log.info('Password reset code sent', { action: 'forgotPassword', email });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Password reset failed');
      log.error('Password reset request failed', error, { action: 'forgotPassword', email });
      captureError(error, { action: 'forgotPassword', email });
      const userMessage = getUserFriendlyError(error);
      setError(userMessage);
      throw new Error(userMessage);
    }
  }, []);

  const confirmForgotPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    log.debug('Confirming password reset', { action: 'confirmForgotPassword', email });
    setError(null);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      log.info('Password reset successful', { action: 'confirmForgotPassword', email });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Password reset confirmation failed');
      log.error('Password reset confirmation failed', error, { action: 'confirmForgotPassword', email });
      captureError(error, { action: 'confirmForgotPassword', email });
      const userMessage = getUserFriendlyError(error);
      setError(userMessage);
      throw new Error(userMessage);
    }
  }, []);

  const updateUserAttributesFn = useCallback(async (attributes: Record<string, string>) => {
    log.debug('Updating user attributes', { action: 'updateUserAttributes', keys: Object.keys(attributes) });
    try {
      const userAttributes: Record<string, string> = {};
      for (const [key, value] of Object.entries(attributes)) {
        // Ensure custom attributes have the 'custom:' prefix
        const attrKey = key.startsWith('custom:') ? key : `custom:${key}`;
        userAttributes[attrKey] = value;
      }
      await amplifyUpdateUserAttributes({
        userAttributes,
      });
      log.info('User attributes updated', { action: 'updateUserAttributes' });
      // Refresh auth state to pick up new attributes
      await checkAuth();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update attributes');
      log.error('Failed to update user attributes', error, { action: 'updateUserAttributes' });
      captureError(error, { action: 'updateUserAttributes' });
      throw error;
    }
  }, [checkAuth]);

  const setAvatarUrl = useCallback((avatarUrl: string | undefined) => {
    setUser(prev => prev ? { ...prev, avatarUrl } : null);
  }, []);

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    getAccessToken,
    login,
    logout,
    register,
    confirmRegistration,
    resendVerificationCode,
    forgotPassword,
    confirmForgotPassword,
    updateUserAttributes: updateUserAttributesFn,
    setAvatarUrl,
  };
}
